import { type ReactNode, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, Settings, MapPin, type LucideIcon } from "lucide-react";
import { BottomNav, Sidebar, MobileHeader, Button, FAB } from "@tabipla/ui";
import { navItems } from "@/config/navigation";
import { useAuth } from "@/application/hooks/useAuth";
import { NotificationBell } from "../components/NotificationBell";

function getInitials(name: string | undefined, email: string | undefined): string {
  if (name) {
    const parts = name.split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return "??";
}

interface FabAction {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
}

interface MekkeLayoutProps {
  children: ReactNode;
  title?: string;
  hideNav?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (query: string) => void;
  fabAction?: FabAction;
}

export function MekkeLayout({
  children,
  title,
  hideNav = false,
  showSearch = false,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  fabAction,
}: MekkeLayoutProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [internalSearchQuery, setInternalSearchQuery] = useState("");

  const searchQuery = searchValue ?? internalSearchQuery;

  const navItemsWithActive = navItems.map((item) => ({
    icon: item.icon,
    label: t(item.labelKey),
    path: item.path,
    isActive: location.pathname === item.path,
  }));

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleSearchChange = (value: string) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearchQuery(value);
    }
  };

  // For pages that hide nav (detail pages), render minimal layout
  if (hideNav) {
    return (
      <div className="min-h-screen bg-background">
        <main>{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <Sidebar
        items={navItemsWithActive}
        onNavigate={handleNavigate}
        header={
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <MapPin className="h-5 w-5" />
              </div>
              <span className="text-xl font-bold text-primary">{t("app.name")}</span>
            </div>
            <NotificationBell />
          </div>
        }
        footer={
          <div className="space-y-3">
            {user && (
              <button
                onClick={() => navigate("/profile")}
                className="flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-accent"
              >
                {user.image ? (
                  <img
                    src={user.image}
                    alt={user.name || ""}
                    className="h-9 w-9 flex-shrink-0 rounded-full object-cover"
                  />
                ) : user.avatarEmoji && user.avatarColor ? (
                  <div
                    className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${user.avatarColor} text-lg`}
                  >
                    {user.avatarEmoji}
                  </div>
                ) : (
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
                    {getInitials(user.name, user.email)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{user.name || "ユーザー"}</p>
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                </div>
                <Settings className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              </button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={logout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              {t("auth.logout")}
            </Button>
          </div>
        }
      />

      {/* Mobile Header */}
      <MobileHeader
        title={title}
        leftAction={
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <MapPin className="h-4 w-4" />
            </div>
            <span className="text-lg font-bold text-primary">{t("app.name")}</span>
          </div>
        }
        rightAction={<NotificationBell />}
        showSearch={showSearch}
        searchPlaceholder={searchPlaceholder}
        searchValue={searchQuery}
        onSearchChange={handleSearchChange}
      />

      {/* Main Content */}
      <main className="pb-24 lg:ml-64 lg:pb-6">
        <div className="container mx-auto px-4 py-6">{children}</div>
      </main>

      {/* Desktop FAB */}
      {fabAction && (
        <FAB
          icon={fabAction.icon}
          label={fabAction.label}
          onClick={fabAction.onClick}
          className="hidden lg:flex"
        />
      )}

      {/* Mobile Bottom Navigation with FAB */}
      <BottomNav items={navItemsWithActive} onNavigate={handleNavigate} fabAction={fabAction} />
    </div>
  );
}
